import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

i18n
    .use(HttpBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        fallbackLng: 'en',
        supportedLngs: ['en', 'ar'],
        debug: import.meta.env.DEV, // Enable debug in development

        interpolation: {
            escapeValue: false, // React already safes from xss
        },

        backend: {
            loadPath: '/locales/{{lng}}/{{ns}}.json',
        },

        detection: {
            order: ['localStorage', 'navigator'],
            caches: ['localStorage'],
        },
    });

// --- Global RTL / lang application (Task #32) ------------------------
//
// Pre-#32, `<LanguageSwitcher>` set `document.documentElement.dir` and
// `.lang` from a component-level `useEffect`. That had two failure
// modes:
//   1. Routes that don't render the switcher (e.g. /login, public
//      profile, error pages) had no dir/lang applied → flash of
//      LTR-when-Arabic, broken Arabic layouts on first paint.
//   2. The effect ran AFTER first render → at minimum one wrong-
//      direction frame even on routes that do render the switcher.
//
// Now: the application is global to the i18n module. One listener,
// one bootstrap call, no component coupling. Works on every route.

function applyDirAndLang(lng: string): void {
    if (typeof document === 'undefined') return;
    const isRtl = lng === 'ar' || lng.startsWith('ar-');
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    document.documentElement.lang = lng;
}

// Bootstrap: apply as soon as i18next resolves the initial language.
// (i18next.init is async; `language` is undefined until detection
// completes, so we either apply on the `initialized` event or, if
// already initialized synchronously from the localStorage detector,
// fire once now.)
if (i18n.isInitialized && i18n.language) {
    applyDirAndLang(i18n.language);
} else {
    i18n.on('initialized', () => {
        applyDirAndLang(i18n.language || 'en');
    });
}

// Live updates: any `i18n.changeLanguage(lng)` call (from the
// LanguageSwitcher or programmatic) propagates to the document.
i18n.on('languageChanged', (lng: string) => {
    applyDirAndLang(lng);
});

export default i18n;
